import axios, {AxiosError, AxiosRequestConfig} from "axios";
import {ErrorResponse} from "../models/response/ErrorResponse";
import {AuthResponse} from "../models/response/AuthResponse";

export const API_URL = process.env.REACT_APP_API_URL;

const $api = axios.create({
    withCredentials: true,
    baseURL: API_URL,
});

type RetryConfig = AxiosRequestConfig & { _isRetry?: boolean };
type QueueItem = {
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
};

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach(({resolve, reject}) => {
        if (error) {
            reject(error);
            return;
        }
        if (token) {
            resolve(token);
        }
    });
    failedQueue = [];
};

$api.interceptors.request.use((config) => {
    config.headers!.Authorization = `Bearer ${localStorage.getItem("token")}`;
    // config.headers!['Content-Type'] = 'application/json';
    if (config.data instanceof FormData) {
        config.headers!["Content-Type"] = "multipart/form-data";
    } else {
        config.headers!["Content-Type"] = "application/json";
    }
    return config;
});

$api.interceptors.response.use(config => config, async (error: AxiosError<ErrorResponse>) => {
    const originalRequest = error.config as RetryConfig;
    const requestUrl = originalRequest?.url || "";

    if (error.response?.status === 401 && originalRequest && !originalRequest._isRetry && !requestUrl.includes("/api/user/refresh")) {
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({
                    resolve: (token: string) => {
                        originalRequest.headers = originalRequest.headers || {};
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve($api.request(originalRequest));
                    },
                    reject,
                });
            });
        }

        originalRequest._isRetry = true;
        isRefreshing = true;

        try {
            const response = await axios.get<AuthResponse>(`${API_URL}/api/user/refresh`, {withCredentials: true});
            const newToken = response.data.accessToken;
            localStorage.setItem("token", newToken);
            processQueue(null, newToken);
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return $api.request(originalRequest!);
        } catch (e) {
            processQueue(e, null);
            throw e;
        } finally {
            isRefreshing = false;
        }
    }
    throw error;
});

export default $api;
